import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import {
  RefreshCcw, ShieldCheck, AlertTriangle, CreditCard, Clock, Mail, Phone, ArrowLeft, XCircle,
} from 'lucide-react';

export default function RefundPolicyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Overview',
      icon: ShieldCheck,
      content: [
        {
          subtitle: 'No-Refund Policy',
          text: 'All bookings, table reservations, cover charges, event tickets, VIP minimums and pre-paid amounts on Scratch Unleashed (operated by Charms Hospitality Services) are STRICTLY NON-REFUNDABLE once confirmed. This policy is communicated at checkout and accepted by the guest at the time of payment.',
        },
        {
          subtitle: 'Why?',
          text: 'As a hospitality venue with limited capacity and time-bound nightly operations, every confirmed booking blocks inventory (table, capacity slot, staff and bar pre-stocking) that cannot be resold once the event night begins. The non-refundable policy ensures fairness to all guests and operational stability of the venue.',
        },
      ],
    },
    {
      title: 'Exceptions Where Refunds Are Issued',
      icon: RefreshCcw,
      content: [
        {
          subtitle: 'Venue-Initiated Cancellations',
          text: 'If Scratch Unleashed cancels an event, closes the venue on the booked date, or is unable to honour your confirmed booking for any reason on our side (including force majeure, government order, or capacity issues caused by us), you will receive a 100% refund to the original payment method.',
        },
        {
          subtitle: 'Duplicate or Failed Transactions',
          text: 'If you were charged twice for the same booking, or if a payment was deducted but no booking was confirmed in our system due to a payment-gateway failure, the duplicate / orphan amount will be fully refunded.',
        },
        {
          subtitle: 'Unauthorised / Fraudulent Charges',
          text: 'If you successfully establish to us and to the payment gateway that a transaction was unauthorised, the amount will be refunded after investigation in line with the gateway dispute resolution timelines.',
        },
      ],
    },
    {
      title: 'How Refunds Are Processed',
      icon: CreditCard,
      content: [
        {
          subtitle: 'Original Payment Method',
          text: 'All eligible refunds are issued to the original payment method only — UPI, debit card, credit card, net banking or wallet used at the time of booking. We do not refund to alternative accounts.',
        },
        {
          subtitle: 'Timeline',
          text: 'Refunds are initiated by us within 3 business days of approval. The amount typically reflects in your account within 7-10 business days of initiation, depending on your bank or card issuer. UPI refunds are usually faster (24-72 hours).',
        },
        {
          subtitle: 'Gateway & Service Fees',
          text: 'Any payment-gateway fees or convenience charges levied by third parties at the time of the original transaction are non-refundable, even where the booking itself qualifies for a refund. The refund is on the base booking amount only.',
        },
      ],
    },
    {
      title: 'No-Show & Late Arrival',
      icon: XCircle,
      content: [
        {
          subtitle: 'No-Shows',
          text: 'If you fail to arrive at the venue on the date and within the entry window of your confirmed booking, the booking is treated as consumed. No refund or rescheduling is offered for no-shows.',
        },
        {
          subtitle: 'Refused Entry',
          text: 'Guests refused entry due to dress code violations, intoxication, behaviour violating venue policies, failure to produce valid age proof, or for any reason within our published House Rules forfeit the booking amount. No refund will be issued in such cases.',
        },
        {
          subtitle: 'Late Arrival',
          text: 'Bookings have a defined entry window communicated at the time of booking. Arrivals after the window may lose priority seating or table allocation; no refund is offered for late arrivals.',
        },
      ],
    },
    {
      title: 'How to Request a Refund',
      icon: Mail,
      content: [
        {
          subtitle: 'Step 1 — Contact Us',
          text: 'Email admin@scratchjsr.com or WhatsApp/Call +91 78703 69666 within 7 days of the original booking date. Include: booking ID, registered phone number, transaction reference (UPI / order ID), and reason for the refund request.',
        },
        {
          subtitle: 'Step 2 — Review',
          text: 'Our team will respond within 3 business days with a decision based on this Refund Policy. If approved, you will receive a confirmation with the expected refund timeline.',
        },
        {
          subtitle: 'Step 3 — Receipt',
          text: 'Approved refunds reach your account within 7-10 business days. If you have not received the refund by day 12 after our confirmation, contact us immediately and we will trace it with the payment gateway.',
        },
      ],
    },
    {
      title: 'Chargebacks & Disputes',
      icon: AlertTriangle,
      content: [
        {
          subtitle: 'Please Contact Us First',
          text: 'Before raising a chargeback with your bank or card network, please contact us first at admin@scratchjsr.com. Most issues are resolved within 3 business days of direct communication, far faster than the bank-mediated chargeback process which typically takes 60-90 days.',
        },
        {
          subtitle: 'Fraudulent Chargebacks',
          text: 'Chargebacks raised for valid, consumed bookings (including bookings consumed by no-shows, refused entry, or genuine attendance) are considered fraudulent. We reserve the right to dispute such chargebacks with full evidence, ban the user account, and pursue legal action under applicable Indian law.',
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Refund &amp; Cancellation Policy | Scratch Unleashed</title>
        <meta
          name="description"
          content="Refund &amp; Cancellation Policy for Scratch Unleashed bookings — operated by Charms Hospitality Services. Non-refundable bookings, venue-initiated cancellations, dispute and chargeback handling."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://scratchjsr.com/refund-policy" />
        <meta property="og:title" content="Refund &amp; Cancellation Policy | Scratch Unleashed" />
        <meta
          property="og:description"
          content="How refunds work at Scratch Unleashed (Charms Hospitality Services) — non-refundable bookings, exceptions, timelines and dispute process."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/refund-policy" />
      </Helmet>

      <div data-testid="refund-policy-page" className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="fixed top-6 left-6 z-50">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            data-testid="refund-back-button"
            className="gap-2 text-[#B9B9B9] hover:text-[#F2F2F2]"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>

        <section className="relative pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
              style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)' }}
            >
              <RefreshCcw size={32} style={{ color: '#D4A843' }} />
            </div>
            <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl font-bold text-[#F2F2F2] mb-4">
              Refund &amp; Cancellation Policy
            </h1>
            <p className="text-lg text-[#B9B9B9] max-w-2xl mx-auto">
              Bookings are non-refundable except in specific cases. We aim to be transparent about how money flows.
            </p>
            <p className="text-sm text-[#6B6B6B] mt-4">
              <strong>Last Updated:</strong> January 2026
            </p>
          </div>
        </section>

        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {sections.map((section, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
                data-testid={`refund-section-${idx}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <section.icon size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, i) => (
                    <div key={i}>
                      {item.subtitle && (
                        <h3 className="text-lg font-semibold text-[#D4A843] mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-[#B9B9B9] leading-relaxed whitespace-pre-line">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:admin@scratchjsr.com"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#D4A843] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="refund-email-cta"
              >
                <Mail size={24} className="mx-auto mb-3 text-[#D4A843]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">Email Us</h3>
                <p className="text-sm text-[#B9B9B9]">admin@scratchjsr.com</p>
              </a>
              <a
                href="https://wa.me/917870369666"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#25D366] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
                data-testid="refund-whatsapp-cta"
              >
                <Phone size={24} className="mx-auto mb-3 text-[#25D366]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">WhatsApp / Phone</h3>
                <p className="text-sm text-[#B9B9B9]">+91 78703 69666</p>
              </a>
            </div>
          </div>
        </section>

        <footer className="border-t border-[rgba(255,255,255,0.07)] py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-[#6B6B6B]">
              © {new Date().getFullYear()} Charms Hospitality Services. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              <button onClick={() => navigate('/terms-and-conditions')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Terms &amp; Conditions</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/privacy-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Privacy Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/shipping-policy')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Shipping Policy</button>
              <span className="text-[#6B6B6B]">·</span>
              <button onClick={() => navigate('/contact')} className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">Contact Us</button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
